import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { knowledgeApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MobileInstallGuide } from '@/components/knowledge/MobileInstallGuide';
import {
  ArrowLeft, Download, Loader2, FileText, Users, GraduationCap,
  Monitor, Heart, ImageIcon, CheckCircle2, AlertTriangle, HelpCircle, Shield
} from 'lucide-react';

const CAT_STYLE = {
  personal: { icon: Users, style: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' },
  skolledning: { icon: GraduationCap, style: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20' },
  it: { icon: Monitor, style: 'text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-900/20' },
  vardnadshavare: { icon: Heart, style: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20' },
  skyltar: { icon: ImageIcon, style: 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-900/20' },
};

const CONTENT = {
  atgardsplan: AtgardsplanContent,
  vardnadshavarbrev: VardnadshavarbrevContent,
  elevinfo: ElevinfoContent,
  skyltar: SkyltarContent,
  implementeringsguide: ImplementeringsguideContent,
  faq: FaqContent,
  'ny-personal': NyPersonalContent,
  'it-guide': ItGuideContent,
  integritet: IntegritetContent,
  'mobil-installation': MobileInstallGuide,
};

/* ─── Shared components ─── */

function H2({ children }) {
  return <h2 className="font-chivo font-bold text-base text-navy dark:text-white mt-8 mb-2 first:mt-0">{children}</h2>;
}
function P({ children }) {
  return <p className="text-sm text-slate-600 dark:text-slate-300 font-ibm leading-relaxed mb-3">{children}</p>;
}
function Note({ children, variant = 'info' }) {
  const s = { info: 'border-blue-200 bg-blue-50/60 dark:bg-blue-900/10', warning: 'border-amber-200 bg-amber-50/60 dark:bg-amber-900/10' };
  return <div className={`rounded-lg border p-4 my-4 text-sm font-ibm ${s[variant]}`}>{children}</div>;
}
function Step({ n, title, children }) {
  return (
    <div className="flex gap-3 mb-5">
      <span className="w-7 h-7 rounded-full bg-navy text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{n}</span>
      <div>
        <p className="font-ibm font-semibold text-sm text-navy dark:text-white">{title}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 font-ibm mt-1 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}
function Check({ children }) {
  return (
    <label className="flex items-start gap-2.5 mb-2.5 cursor-pointer group">
      <input type="checkbox" className="mt-0.5 rounded border-slate-300 h-4 w-4" />
      <span className="text-sm text-slate-600 dark:text-slate-300 font-ibm leading-relaxed">{children}</span>
    </label>
  );
}
function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300 font-ibm leading-relaxed">
      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}

/* ─── ÅTGÄRDSPLAN ─── */

function AtgardsplanContent() {
  return (
    <div>
      <P>Denna åtgärdsplan beskriver hur skolpersonal ska agera när Vapelarm Cloud registrerar ett larm. Planen kan skrivas ut och placeras i personalrummet eller vid arbetsplatsen.</P>

      <H2>Steg-för-steg vid larm</H2>
      <Step n="1" title="Ta emot larmet">Du får en push-notifikation på din telefon via Vapelarm Cloud-appen. Notifikationen visar vilket rum larmet gäller. Gå omedelbart till platsen.</Step>
      <Step n="2" title="Kontrollera rummet">Gå in i rummet och observera. Känner du en söt, kemisk eller fruktliknande lukt? Finns det elever i närheten? Är rummet tomt?</Step>
      <Step n="3" title="Bedöm situationen">Avgör om det rör sig om vaping, spray/deodorant eller om inget ovanligt kan konstateras. Du behöver inte vara säker — din bedömning registreras som underlag för systemets AI.</Step>
      <Step n="4" title="Ge feedback i appen">Öppna Vapelarm Cloud och välj ett av alternativen:
        <br /><strong>Bekräfta vape</strong> — du bedömer att vaping har skett.
        <br /><strong>Spray/deodorant</strong> — larmet orsakades av spray.
        <br /><strong>Inget hittades</strong> — du kan inte bekräfta något ovanligt.</Step>
      <Step n="5" title="Följ skolans handlingsplan">Om vaping bekräftas, följ skolans ordinarie rutin. Kontakta elevhälsan om det gäller en identifierad elev. Alla åtgärder dokumenteras i systemet.</Step>
      <Step n="6" title="Dokumentera vid behov">Vid bekräftad vaping skapas automatiskt ett ärende i Vapelarm Cloud. Lägg till eventuella anteckningar om vad du observerade.</Step>

      <Note variant="warning">
        <p className="font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5 mb-1">
          <AlertTriangle className="h-4 w-4" /> Viktig information
        </p>
        <p className="text-amber-600 dark:text-amber-400">Din feedback är avgörande för systemets precision. Varje svar — även &quot;Inget hittades&quot; — hjälper AI:n att bli bättre på att skilja vaping från andra orsaker. Ge alltid feedback, även om du är osäker.</p>
      </Note>
    </div>
  );
}

/* ─── VÅRDNADSHAVARBREV ─── */

function VardnadshavarbrevContent({ templateData, onTemplateChange }) {
  return (
    <div>
      <P>Denna mall genererar ett informationsbrev till vårdnadshavare. Fyll i skolans uppgifter nedan så skapas ett anpassat brev som kan laddas ner som PDF.</P>

      <div className="grid gap-4 sm:grid-cols-2 my-6 p-5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
        <div>
          <Label className="font-ibm text-xs mb-1.5 block">Skolans namn</Label>
          <Input value={templateData.school_name || ''} onChange={e => onTemplateChange('school_name', e.target.value)} placeholder="T.ex. Frykenskolan" className="h-10" data-testid="template-school-name" />
        </div>
        <div>
          <Label className="font-ibm text-xs mb-1.5 block">Rektorns namn</Label>
          <Input value={templateData.principal_name || ''} onChange={e => onTemplateChange('principal_name', e.target.value)} placeholder="T.ex. Anna Svensson" className="h-10" data-testid="template-principal-name" />
        </div>
        <div>
          <Label className="font-ibm text-xs mb-1.5 block">E-postadress</Label>
          <Input value={templateData.contact_email || ''} onChange={e => onTemplateChange('contact_email', e.target.value)} placeholder="rektor@kommun.se" className="h-10" data-testid="template-email" />
        </div>
        <div>
          <Label className="font-ibm text-xs mb-1.5 block">Telefonnummer</Label>
          <Input value={templateData.contact_phone || ''} onChange={e => onTemplateChange('contact_phone', e.target.value)} placeholder="08-123 45 67" className="h-10" data-testid="template-phone" />
        </div>
        <div>
          <Label className="font-ibm text-xs mb-1.5 block">Datum</Label>
          <Input type="date" value={templateData.date || ''} onChange={e => onTemplateChange('date', e.target.value)} className="h-10" data-testid="template-date" />
        </div>
      </div>

      <H2>Brevets innehåll</H2>
      <P>Brevet förklarar för vårdnadshavare:</P>
      <ul className="space-y-2 my-3">
        <Bullet>Varför skolan arbetar förebyggande mot vaping</Bullet>
        <Bullet>Att luftkvalitetssensorer har installerats i utvalda utrymmen</Bullet>
        <Bullet>Att sensorerna inte innehåller kamera, mikrofon eller annan övervakningsutrustning</Bullet>
        <Bullet>Att systemet inte kan identifiera enskilda elever</Bullet>
        <Bullet>Hur skolan agerar vid ett registrerat larm</Bullet>
      </ul>
      <P>Ladda ner brevet som PDF med skolans uppgifter ifyllda. Brevet är utformat för att kunna skickas hem digitalt eller i pappersform.</P>
    </div>
  );
}

/* ─── ELEVINFO ─── */

function ElevinfoContent() {
  return (
    <div>
      <H2>Varför finns Vapelarm på vår skola?</H2>
      <P>Vapelarm är ett system som hjälper skolan att skapa en hälsosammare inomhusmiljö. I utvalda utrymmen, som toaletter och omklädningsrum, sitter små sensorer som mäter luftkvaliteten.</P>

      <H2>Vad mäter sensorerna?</H2>
      <P>Sensorerna mäter partiklar, kemiska ämnen, temperatur och luftfuktighet i luften. Med hjälp av AI-teknik kan systemet upptäcka förändringar som kan tyda på att någon har använt e-cigarett (vaping). Systemet kan även skilja mellan vaping och vanlig deodorant eller spray.</P>

      <H2>Vad gör sensorerna inte?</H2>
      <Note variant="info">
        <ul className="space-y-2">
          <Bullet>Ingen kamera — sensorerna kan inte se något</Bullet>
          <Bullet>Ingen mikrofon — sensorerna kan inte höra något</Bullet>
          <Bullet>Ingen identifiering — systemet vet inte vem som befinner sig i rummet</Bullet>
        </ul>
      </Note>
      <P>Sensorerna mäter enbart luften. De registrerar inga bilder, inget ljud och ingen information om enskilda personer.</P>

      <H2>Varför gör skolan detta?</H2>
      <P>Vaping innebär hälsorisker, särskilt för unga vars lungor fortfarande utvecklas. Skolan har ett ansvar att erbjuda en rökfri och hälsosam miljö för alla. Vapelarm är ett av verktygen i det arbetet.</P>

      <H2>Vad händer om sensorn registrerar något?</H2>
      <P>Om sensorn upptäcker förändringar i luftkvaliteten som kan tyda på vaping, får ansvarig personal en diskret notifikation. Personalen kontrollerar sedan platsen och bedömer situationen. Eventuella åtgärder följer skolans ordinarie rutiner — det är alltid en person som fattar beslutet, inte tekniken.</P>
    </div>
  );
}

/* ─── SKYLTAR ─── */

function SkyltarContent() {
  return (
    <div>
      <H2>Utskrivbara skyltar</H2>
      <P>Ladda ner och skriv ut skyltar att placera i utrymmen som övervakas med Vapelarm. Skyltarna informerar om att luftkvalitetssensor finns installerad och förklarar att varken kamera, mikrofon eller ljudinspelning används.</P>

      <div className="grid gap-4 sm:grid-cols-2 my-6">
        {[
          { size: 'a4', label: 'A4-skylt', desc: 'Full storlek — för väggar och anslagstavlor' },
          { size: 'a5', label: 'A5-skylt', desc: 'Kompakt storlek — för dörrar och mindre ytor' },
        ].map(s => (
          <div key={s.size} className="border border-slate-200 dark:border-slate-700 rounded-lg p-5 text-center">
            <div className="w-20 h-28 mx-auto mb-3 bg-slate-900 rounded-sm flex flex-col items-center justify-center gap-1 p-2">
              <Shield className="h-4 w-4 text-white" />
              <span className="text-white text-[6px] font-ibm font-bold leading-tight">VAPNINGSFRI ZON</span>
            </div>
            <p className="font-ibm font-semibold text-sm text-navy dark:text-white">{s.label}</p>
            <p className="text-xs text-slate-400 font-ibm mt-0.5">{s.desc}</p>
          </div>
        ))}
      </div>

      <H2>Text på skylten</H2>
      <Note variant="info">
        <p className="text-slate-600 dark:text-slate-300 italic">&quot;VAPNINGSFRI ZON — Detta utrymme är utrustat med luftkvalitetssensor som kan upptäcka vaping. Ingen kamera. Ingen mikrofon. Ingen ljudinspelning.&quot;</p>
      </Note>
    </div>
  );
}

/* ─── IMPLEMENTERINGSGUIDE ─── */

function ImplementeringsguideContent() {
  return (
    <div>
      <P>Denna guide vägleder dig som rektor eller skolledare genom hela processen att införa Vapelarm på din skola. Markera varje steg som klart allteftersom arbetet fortskrider.</P>

      <H2>Checklista för implementering</H2>
      <div className="space-y-1 mt-4 mb-6">
        <Check>Identifiera utsatta utrymmen — toaletter, omklädningsrum och andra platser där vaping förekommer.</Check>
        <Check>Installera sensorer — montera enligt installationsguiden. WiFi-sensorer kräver 2,4 GHz-nätverk.</Check>
        <Check>Bestäm vilka som ska få larm — skapa konton i Vapelarm Cloud med rätt roller.</Check>
        <Check>Informera personalen — ge tillgång till Personalguiden och genomför en kort introduktion.</Check>
        <Check>Informera elever och vårdnadshavare — använd mallarna i Kunskapsbanken. Var transparent.</Check>
        <Check>Testa larmflödet — utlös ett testlarm och verifiera att rätt personer får notifikationer.</Check>
        <Check>Starta driften — aktivera sensorerna och sätt upp skyltar i de övervakade utrymmena.</Check>
        <Check>Följ statistik och incidenter — använd dashboarden för att följa trender.</Check>
        <Check>Utvärdera efter 30 dagar — har antalet incidenter minskat? Behöver inställningar justeras?</Check>
      </div>

      <Note variant="info">
        <p className="text-slate-600 dark:text-slate-300"><strong>Tips:</strong> Erfarenheten visar att vaping ofta minskar markant redan inom de första veckorna efter installation. Enbart vetskapen om att sensorerna finns har en förebyggande effekt.</p>
      </Note>
    </div>
  );
}

/* ─── FAQ ─── */

function FaqContent() {
  const faqs = [
    { q: "Filmar sensorn?", a: "Nej. Sensorn har ingen kamera och kan varken ta bilder eller spela in video. Den mäter enbart luftkvalitet." },
    { q: "Spelar sensorn in ljud?", a: "Nej. Sensorn har ingen mikrofon och kan inte spela in ljud, samtal eller andra ljud i rummet." },
    { q: "Kan systemet identifiera vilken elev som befinner sig i rummet?", a: "Nej. Vapelarm mäter enbart kemiska förändringar i luften. Systemet har ingen möjlighet att registrera, identifiera eller spåra enskilda personer." },
    { q: "Vad mäter sensorn exakt?", a: "Sensorn mäter partiklar (PM1, PM2.5 och PM10), flyktiga organiska ämnen (VOC), temperatur och luftfuktighet. Dessa värden analyseras av en AI-algoritm som kan skilja vaping från andra partikkelkällor." },
    { q: "Vad händer när ett larm registreras?", a: "Utvald personal får en diskret notifikation via appen. Personalen kontrollerar platsen, bedömer situationen och ger feedback i systemet. Eventuella åtgärder följer skolans ordinarie rutiner." },
    { q: "Kan deodorant eller spray utlösa ett falskt larm?", a: "Systemet är utvecklat för att skilja mellan vaping och vanlig spray. AI-algoritmen analyserar partikelprofil, VOC-mönster och fuktighetsförändringar för att minimera falsklarm. Personalen kan markera eventuella falsklarm i appen, vilket ytterligare förbättrar precisionen." },
    { q: "Var lagras data?", a: "All data lagras säkert i en krypterad molndatabas. Överföring sker via HTTPS med TLS-kryptering. Ingen personlig data om elever samlas in eller lagras." },
    { q: "Vem har tillgång till larmen?", a: "Enbart behörig personal med ett aktivt konto i Vapelarm Cloud. Åtkomst styrs via roller och behörigheter — varje användare ser enbart den information som är relevant för sin roll." },
    { q: "Hur länge sparas larmhistoriken?", a: "Sensordata och larmhistorik sparas så länge skolan har en aktiv licens. Administratören kan vid behov radera historik." },
    { q: "Vad gör skolan efter ett bekräftat larm?", a: "Personalen följer skolans åtgärdsplan: kontroll av platsen, dokumentation i systemet och vid behov kontakt med elevhälsa eller vårdnadshavare. Alla åtgärder loggas i ärendehanteringen." },
  ];

  return (
    <div>
      <P>Här hittar du svar på de vanligaste frågorna om Vapelarm Cloud. Informationen riktar sig till personal, skolledning och vårdnadshavare.</P>
      <div className="space-y-2 mt-4">
        {faqs.map((faq, i) => (
          <details key={i} className="group border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <summary className="flex items-center gap-3 p-4 cursor-pointer font-ibm font-semibold text-sm text-navy dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors select-none">
              <HelpCircle className="h-4 w-4 text-slate-300 shrink-0 group-open:text-blue-500 transition-colors" />
              <span className="flex-1">{faq.q}</span>
            </summary>
            <div className="px-4 pb-4 pt-0">
              <p className="text-sm text-slate-500 dark:text-slate-400 font-ibm leading-relaxed pl-7">{faq.a}</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

/* ─── NY PERSONAL ─── */

function NyPersonalContent() {
  return (
    <div>
      <P>Välkommen som användare av Vapelarm Cloud. Den här guiden ger dig allt du behöver för att komma igång.</P>

      <H2>Vad är Vapelarm?</H2>
      <P>Vapelarm är ett AI-drivet övervakningssystem för luftkvalitet som hjälper skolan att upptäcka vaping i realtid. Sensorer placerade i utvalda utrymmen analyserar luften och skickar en notifikation till dig om förändringar registreras som kan tyda på e-cigarettanvändning.</P>

      <H2>Hur får jag larm?</H2>
      <P>Du får larm via Vapelarm Cloud-appen på din telefon. Appen installeras som en PWA — öppna webbsidan i din webbläsare och välj &quot;Lägg till på hemskärm&quot;. Aktivera push-notiser när appen frågar. Du kan även få larm via Microsoft Teams om skolan har aktiverat det.</P>

      <H2>Vad gör jag vid larm?</H2>
      <Step n="1" title="Kontrollera rummet">Gå till det rum som anges i notifikationen.</Step>
      <Step n="2" title="Bedöm situationen">Känns det som vaping, spray eller ingenting?</Step>
      <Step n="3" title="Ge feedback">Öppna appen och tryck på rätt knapp: Bekräfta vape, Spray eller Inget hittades.</Step>
      <Step n="4" title="Följ skolans rutin">Vid bekräftad vaping — följ skolans handlingsplan.</Step>

      <H2>Vem kontaktar jag vid problem?</H2>
      <P>Kontakta din skoladministratör vid frågor om rutiner. Vid tekniska problem, kontakta IT-ansvarig eller Vapelarm support på support@vapelarm.se.</P>
    </div>
  );
}

/* ─── IT-GUIDE ─── */

function ItGuideContent() {
  return (
    <div>
      <H2>Sensoranslutning</H2>
      <P><strong>WiFi (2,4 GHz)</strong> — WiFi-sensorerna ansluter till ett 2,4 GHz trådlöst nätverk. Sensorn konfigureras via tillverkarens mobilapp med SSID och lösenord. Data överförs via HTTPS till Vapelarm Cloud. Se till att nätverket tillåter utgående HTTPS-trafik (port 443).</P>
      <P><strong>LoRaWAN</strong> — LoRaWAN-sensorerna kommunicerar via The Things Network (TTN). En LoRaWAN-gateway måste finnas inom räckvidd. Data vidarebefordras automatiskt till Vapelarm Cloud via webhook.</P>

      <H2>Nätverkskrav</H2>
      <P>Sensorerna kräver utgående HTTPS (port 443). Ingen inkommande port behöver öppnas. Brandväggsregler ska tillåta trafik till Vapelarm Clouds domäner. Bandbreddskravet är minimalt — varje sensor skickar cirka 1 KB data var 60:e sekund.</P>

      <H2>Microsoft Teams-integration</H2>
      <P>Skapa en inkommande webhook i den Teams-kanal som ska ta emot larm. Klistra in webhook-URL:en i Vapelarm Clouds inställningar under Aviseringar. Testa med en testavisering för att verifiera att kopplingen fungerar.</P>

      <H2>PWA / Personalapp</H2>
      <P>Vapelarm Cloud installeras som en Progressive Web App (PWA). Öppna appen i Safari (iOS) eller Chrome (Android) och välj &quot;Lägg till på hemskärm&quot;. På iOS 16.4 och senare krävs att appen är installerad som PWA för att push-notiser ska fungera.</P>

      <H2>Användarkonton och roller</H2>
      <P><strong>Superadmin</strong> — full åtkomst till alla inställningar, skolor och data.<br />
      <strong>Skoladmin</strong> — hanterar sin skolas användare, rum och inställningar.<br />
      <strong>Personal</strong> — tar emot larm och ger feedback.<br />
      <strong>Kommunadmin</strong> — överblick över alla skolor inom kommunen.</P>

      <H2>Felsökning</H2>
      <P><strong>Sensorn syns inte i systemet:</strong> Kontrollera att sensorn är ansluten till rätt nätverk och har strömförsörjning. Verifiera att Reading Forwarding är aktiverat i app.vapelarm.se.</P>
      <P><strong>Inga larm kommer:</strong> Kontrollera att push-notiser är aktiverade i appen och att larmprofilen är korrekt konfigurerad. Använd Testlarm-funktionen i Vapelarm Cloud.</P>
      <P><strong>Support:</strong> Kontakta Vapelarm på support@vapelarm.se eller via chatten i appen.</P>
    </div>
  );
}

/* ─── INTEGRITET ─── */

function IntegritetContent() {
  return (
    <div>
      <P>Denna sida sammanfattar hur Vapelarm Cloud hanterar data, integritet och säkerhet. Informationen kan användas som underlag vid kommunikation med dataskyddsombud och vid integritetsgranskningar.</P>

      <H2>Data som samlas in</H2>
      <P>Sensorerna mäter uteslutande luftkvalitetsparametrar: partikelnivåer (PM1, PM2.5, PM10), flyktiga organiska ämnen (VOC), temperatur och luftfuktighet. Ingen biologisk, biometrisk eller personidentifierbar data samlas in.</P>

      <H2>Kamera och mikrofon</H2>
      <P>Vapelarm-sensorerna innehåller varken kamera, mikrofon eller annan utrustning för bild- eller ljudinspelning. Det är tekniskt omöjligt för sensorn att identifiera, fotografera eller avlyssna personer.</P>

      <H2>Personidentifiering</H2>
      <P>Systemet kan inte identifiera vilka personer som befinner sig i ett rum. Larm baseras uteslutande på kemiska förändringar i luften — inte på närvaro av specifika individer.</P>

      <H2>Åtkomst och roller</H2>
      <P>All åtkomst till Vapelarm Cloud styrs via rollbaserad behörighet. Varje användare ser enbart data som är relevant för sin roll. Åtkomstnivåerna är: Superadmin, Skoladmin, Personal och Kommunadmin.</P>

      <H2>Lagring och kryptering</H2>
      <P>All data lagras i en krypterad molndatabas. Överföring mellan sensor, server och klient sker via HTTPS med TLS-kryptering. Lösenord lagras med bcrypt-hashning.</P>

      <Note variant="warning">
        <p className="font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5 mb-2">
          <AlertTriangle className="h-4 w-4" /> Att komplettera av skolans administration
        </p>
        <ul className="space-y-1.5 text-amber-600 dark:text-amber-400">
          <li>Ansvarig personuppgiftsombud (DPO)</li>
          <li>Beslutad lagringstid för sensordata och larmhistorik</li>
          <li>Rutiner för radering av data vid avslutad licens</li>
          <li>Eventuella avtal med underleverantörer</li>
          <li>Konsekvensbedömning (DPIA) om sådan har genomförts</li>
        </ul>
      </Note>
    </div>
  );
}

/* ─── MAIN PAGE ─── */

export default function KnowledgeDocumentPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [templateData, setTemplateData] = useState({});

  useEffect(() => { loadDocument(); }, [slug]);

  const loadDocument = async () => {
    try {
      const res = await knowledgeApi.document(slug);
      setDoc(res.data);
    } catch {
      toast.error('Dokumentet kunde inte laddas');
      navigate('/knowledge');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (field, value) => setTemplateData(prev => ({ ...prev, [field]: value }));

  const handleDownload = async () => {
    if (!doc?.has_pdf) return;
    setDownloading(true);
    try {
      const endpoint = doc.pdf_endpoint.startsWith('/api/') ? doc.pdf_endpoint.replace('/api/', '/') : doc.pdf_endpoint;
      const params = doc.pdf_type === 'template' ? templateData : {};
      const res = await api.get(endpoint, { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vapelarm_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${doc.title} nedladdad som PDF`);
    } catch {
      toast.error('Kunde inte ladda ner PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="p-6 md:p-10 flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  if (!doc) return null;

  const cat = CAT_STYLE[doc.category] || CAT_STYLE.personal;
  const CatIcon = cat.icon;
  const ContentRenderer = CONTENT[doc.content];

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto space-y-6" data-testid="knowledge-document-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/knowledge')} className="gap-1.5 text-slate-400 -ml-2" data-testid="back-to-knowledge-btn">
        <ArrowLeft className="h-4 w-4" /> Kunskapsbank
      </Button>

      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cat.style}`}>
          <CatIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-chivo font-black text-xl md:text-2xl text-navy dark:text-white tracking-tight leading-tight">{doc.title}</h1>
          <p className="text-slate-400 mt-1 font-ibm text-sm">{doc.description}</p>
        </div>
      </div>

      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-5 md:p-8">
          {ContentRenderer ? (
            <ContentRenderer templateData={templateData} onTemplateChange={handleTemplateChange} />
          ) : (
            <div className="py-12 text-center">
              <FileText className="h-10 w-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 font-ibm text-sm">Detta dokument är tillgängligt som PDF.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {doc.has_pdf && (
        <div className="flex justify-end">
          <Button onClick={handleDownload} disabled={downloading} className="gap-2" data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Ladda ner som PDF
          </Button>
        </div>
      )}
    </div>
  );
}
